import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Response } from './local/Response';
import { map } from 'rxjs/operators';
import { __ } from '../functions/object.functions';
import { CacheSettings } from '@app/core/http/cache.interceptor';
import { hypecastHttpContext } from '@app/core/http/http-context';

export class EntityService<T, TCreate, TUpdate> {
  constructor(
    private endpoint: string,
    private entity: string,
    protected httpClient: HttpClient
  ) {}

  getAll(
    queryParameters: string = '',
    skip: number = 0,
    take: number = 20,
    cacheOptions: CacheSettings | null = null
  ): Observable<T[]> {
    const _skip = __.IsNullOrUndefined(skip) ? 0 : skip;
    const _take = __.IsNullOrUndefined(take) ? 20 : take;
    const _queryParameters = __.IsNullOrUndefinedOrEmpty(queryParameters) ? '' : `&${queryParameters}`;

    return this.httpClient.get<Response<T[]>>(
      `${this.endpoint}?skip=${_skip}&take=${_take}${_queryParameters}`,
      {context: hypecastHttpContext({cache: cacheOptions})},
    ).pipe(
      map((response: Response<T[]>) => response.data)
    );
  }

  getById(
    id: string,
    cacheOptions: CacheSettings | null = null
  ): Observable<T> {
    return this.httpClient.get<Response<T>>(
      `${this.endpoint}/${id}`,
      {context: hypecastHttpContext({cache: cacheOptions})},
    ).pipe(
      map((response: Response<T>) => response.data)
    );
  }

  getAllChildren<TChild>(
    id: string,
    childEndpoint: string,
    queryParameters: string = '',
    skip: number = 0,
    take: number = 20,
    cacheOptions: CacheSettings | null = null
  ): Observable<TChild[]> {
    const _skip = __.IsNullOrUndefined(skip) ? 0 : skip;
    const _take = __.IsNullOrUndefined(take) ? 20 : take;
    const _queryParameters = __.IsNullOrUndefinedOrEmpty(queryParameters) ? '' : `&${queryParameters}`;

    return this.httpClient.get<Response<TChild[]>>(
      `${this.endpoint}/${id}/${childEndpoint}?skip=${_skip}&take=${_take}${_queryParameters}`,
      {context: hypecastHttpContext({cache: cacheOptions, skipErrorHandler: true})},
    ).pipe(
      map((response: Response<TChild[]>) => response.data)
    );
  }

  create(entity: TCreate): Observable<T> {
    return this.httpClient.post<Response<T>>(`${this.endpoint}`, entity).pipe(
      map((response: Response<T>) => response.data)
    );
  }

  update(id: string, entity: TUpdate): Observable<T> {
    return this.httpClient.patch<Response<T>>(`${this.endpoint}/${id}`, entity).pipe(
      map((response: Response<T>) => response.data)
    );
  }

  deleteById(id: string): Observable<boolean> {
    return this.httpClient.delete<Response<boolean>>(`${this.endpoint}/${id}`).pipe(
      map((response: Response<boolean>) => response.data)
    );
  }
}
